/**
 * device-shelf.js — files a person keeps inside a tool, on THIS device.
 *
 * The need, in a support chat (04.11.2026, /document-reader): a guest listened
 * to a 24-page PDF for an hour, the tab reloaded overnight, and in the morning
 * the page was empty again — «можно ли сохранить файл для чтения после
 * перезагрузки страницы?». The reading position survived (it is a setting);
 * the file did not, because a picked File lives only as long as the page.
 *
 * A shelf is the answer readers, players and editors give to that: the file is
 * kept, with what the tool knows about it (where the person stopped, how long
 * it is), and the next visit opens it with one press. Here the bytes stay in
 * this browser's storage: no account, no upload, and no copy on another device
 * or in another browser — the page says exactly that, nothing more.
 *
 * ── Where the bytes live ────────────────────────────────────────────────────
 * In the shared work storage (work-storage.js): the persisted `work` bucket with
 * strict durability where the browser has Storage Buckets, the default
 * IndexedDB elsewhere. A shelf is work in that module's sense — the person put
 * the file there on purpose and nothing on our side can recompute it — so it
 * must not share the fate of model caches when the disk fills.
 *
 * Some WebKit builds refuse Blobs in IndexedDB («Error preparing Blob/File
 * data»); the shelf then stores the bytes as an ArrayBuffer. Probed once, by a
 * real write, not guessed from the user agent.
 *
 * ── Knowing that the browser took it ────────────────────────────────────────
 * An eviction or «clear site data» empties the shelf without a word. Each shelf
 * notes in localStorage how many files it held and under which epoch of the
 * work storage (work-storage.js epoch()). A later visit that finds the shelf
 * empty under a NEW epoch knows the browser deleted it — takeLost() tells the
 * tool, so it can say so instead of looking as if nothing had ever been kept.
 *
 * Exposes window.__deviceShelf:
 *   open(slug)               → Promise<Shelf>  (rejects where IndexedDB is unusable)
 *   reason(err)              → 'quota' | 'closed' | 'other'
 * Shelf:
 *   list()                   → Promise<[record]> newest opened first
 *   put(key, file, fields)   → Promise<record>  bytes + record in one transaction
 *   file(key)                → Promise<File|null>
 *   patch(key, fields)       → Promise<record|null>  fields only, bytes untouched
 *   remove(key)              → Promise<void>
 *   takeLost()               → number of files the browser deleted since the
 *                              last visit (0 when none or unknown); once
 * record = {key, name, type, size, lastModified, addedAt, openedAt, …fields}
 */
(function (global) {
'use strict';
if (global.__deviceShelf) return;

var NAME = 'timbrica_device_shelf';
var VERSION = 1;
var NOTE = 'tmb_shelf_note:';
var SEP = '\u0001';

function upgrade(db) {
    if (!db.objectStoreNames.contains('items')) {
        var items = db.createObjectStore('items', { keyPath: 'id' });
        items.createIndex('slug', 'slug');
    }
    if (!db.objectStoreNames.contains('files')) db.createObjectStore('files', { keyPath: 'id' });
}

function req(r) {
    return new Promise(function (resolve, reject) {
        r.onsuccess = function () { resolve(r.result); };
        r.onerror = function () { reject(r.error || new Error('idb-request')); };
    });
}
function done(t) {
    return new Promise(function (resolve, reject) {
        t.oncomplete = function () { resolve(); };
        t.onerror = function () { reject(t.error || new Error('idb-tx')); };
        t.onabort = function () { reject(t.error || new Error('idb-abort')); };
    });
}

/* Without the shared work storage on the page: the same contract over a plain
   connection, opened again when the browser closes it. */
function plainConnection() {
    var cur = null;
    function open() {
        return new Promise(function (resolve, reject) {
            var r;
            try { r = global.indexedDB.open(NAME, VERSION); } catch (e) { reject(e); return; }
            r.onupgradeneeded = function () { upgrade(r.result); };
            r.onsuccess = function () { resolve(r.result); };
            r.onerror = function () { reject(r.error || new Error('idb-open')); };
            r.onblocked = function () { reject(new Error('idb-blocked')); };
        });
    }
    function db() {
        if (!cur) {
            var p = cur = open().then(function (d) {
                d.addEventListener('close', function () { if (cur === p) cur = null; });
                d.addEventListener('versionchange', function () { try { d.close(); } catch (_) {} if (cur === p) cur = null; });
                return d;
            });
            p.catch(function () { if (cur === p) cur = null; });
        }
        return cur;
    }
    return {
        db: db,
        tx: function (stores, mode) {
            return db().then(function (d) { return d.transaction(stores, mode || 'readwrite'); });
        }
    };
}

function noteKey(slug) { return NOTE + slug; }
function readNote(slug) {
    try { return JSON.parse(global.localStorage.getItem(noteKey(slug)) || 'null'); } catch (_) { return null; }
}
function writeNote(slug, ep, n) {
    try { global.localStorage.setItem(noteKey(slug), JSON.stringify({ ep: ep || '', n: n })); } catch (_) {}
}
function epoch() {
    var ws = global.__workStorage;
    return ws && typeof ws.epoch === 'function' ? ws.epoch() : Promise.resolve(null);
}

function strip(row) {
    var out = {};
    for (var k in row) if (Object.prototype.hasOwnProperty.call(row, k) && k !== 'id' && k !== 'slug') out[k] = row[k];
    return out;
}

function Shelf(conn, slug) {
    this.conn = conn;
    this.slug = slug;
    this._blobs = null;
    this._lost = 0;
    this._protected = false;
}

Shelf.prototype._id = function (key) { return this.slug + SEP + String(key); };

/* A Blob written and read back. WebKit builds that cannot keep Blobs throw on
   the put, not on the read, so the write itself is the answer. */
Shelf.prototype._blobsOk = function () {
    var self = this;
    if (self._blobs !== null) return Promise.resolve(self._blobs);
    return self.conn.tx(['files']).then(function (t) {
        var id = self._id('\u0000probe');
        t.objectStore('files').put({ id: id, b: new Blob(['ok'], { type: 'text/plain' }) });
        t.objectStore('files').delete(id);
        return done(t);
    }).then(function () { self._blobs = true; return true; }, function () { self._blobs = false; return false; });
};

Shelf.prototype._note = function (n) {
    var slug = this.slug;
    return epoch().then(function (ep) { writeNote(slug, ep, n); }, function () {});
};

Shelf.prototype.list = function () {
    var self = this;
    return self.conn.tx(['items'], 'readonly').then(function (t) {
        return req(t.objectStore('items').index('slug').getAll(self.slug));
    }).then(function (rows) {
        var list = (rows || []).map(strip).sort(function (a, b) { return (b.openedAt || 0) - (a.openedAt || 0); });
        return epoch().then(function (ep) {
            var prev = readNote(self.slug);
            if (!list.length && prev && prev.n > 0 && prev.ep && ep && prev.ep !== ep) self._lost = prev.n;
            writeNote(self.slug, ep, list.length);
            return list;
        }, function () { return list; });
    });
};

Shelf.prototype.takeLost = function () { var n = this._lost; this._lost = 0; return n; };

Shelf.prototype.put = function (key, file, fields) {
    var self = this, id = self._id(key), now = Date.now();
    return self._blobsOk().then(function (ok) {
        return ok ? file : file.arrayBuffer();
    }).then(function (bytes) {
        return self.conn.tx(['items', 'files']).then(function (t) {
            var items = t.objectStore('items');
            var rec = null;
            var g = items.get(id);
            g.onsuccess = function () {
                var have = g.result || {};
                rec = {
                    id: id, slug: self.slug, key: String(key),
                    name: file.name || 'file', type: file.type || '', size: file.size || 0,
                    lastModified: file.lastModified || now,
                    addedAt: have.addedAt || now, openedAt: now
                };
                var f = fields || {};
                for (var k in have) if (!(k in rec) && Object.prototype.hasOwnProperty.call(have, k)) rec[k] = have[k];
                for (var j in f) if (Object.prototype.hasOwnProperty.call(f, j) && !(j in { id: 1, slug: 1, key: 1 })) rec[j] = f[j];
                items.put(rec);
                t.objectStore('files').put({ id: id, b: bytes });
            };
            return done(t).then(function () { return strip(rec); });
        });
    }).then(function (rec) {
        if (!self._protected && global.__workStorage && global.__workStorage.protect) {
            self._protected = true;
            global.__workStorage.protect('device-shelf');
        }
        return self.list().then(function () { return rec; }, function () { return rec; });
    });
};

Shelf.prototype.file = function (key) {
    var self = this, id = self._id(key);
    return self.conn.tx(['items', 'files'], 'readonly').then(function (t) {
        return Promise.all([req(t.objectStore('items').get(id)), req(t.objectStore('files').get(id))]);
    }).then(function (got) {
        var rec = got[0], row = got[1];
        if (!rec || !row || row.b == null) return null;
        return new File([row.b], rec.name, { type: rec.type || '', lastModified: rec.lastModified || Date.now() });
    });
};

Shelf.prototype.patch = function (key, fields) {
    var self = this, id = self._id(key);
    return self.conn.tx(['items']).then(function (t) {
        var items = t.objectStore('items'), rec = null;
        var g = items.get(id);
        g.onsuccess = function () {
            if (!g.result) return;
            rec = g.result;
            for (var k in fields) if (Object.prototype.hasOwnProperty.call(fields, k) && !(k in { id: 1, slug: 1, key: 1 })) rec[k] = fields[k];
            items.put(rec);
        };
        return done(t).then(function () { return rec ? strip(rec) : null; });
    });
};

Shelf.prototype.remove = function (key) {
    var self = this, id = self._id(key);
    return self.conn.tx(['items', 'files']).then(function (t) {
        t.objectStore('items').delete(id);
        t.objectStore('files').delete(id);
        return done(t);
    }).then(function () { return self.list().then(function () {}, function () {}); });
};

var _open = {};
function open(slug) {
    slug = String(slug || '');
    if (_open[slug]) return _open[slug];
    var p = _open[slug] = Promise.resolve().then(function () {
        if (!global.indexedDB) throw new Error('no-idb');
        var ws = global.__workStorage;
        var conn = ws && typeof ws.connection === 'function'
            ? ws.connection(NAME, VERSION, upgrade, { stamp: function (v) { return (v && (v.openedAt || v.addedAt)) || 0; } })
            : plainConnection();
        return conn.db().then(function () { return new Shelf(conn, slug); });
    });
    p.catch(function () { if (_open[slug] === p) delete _open[slug]; });
    return p;
}

function reason(err) {
    var n = err && (err.name || '');
    var m = String((err && err.message) || '');
    if (n === 'QuotaExceededError' || (err && err.code === 22) || /quota/i.test(m)) return 'quota';
    if (n === 'InvalidStateError' || /closing|closed/i.test(m)) return 'closed';
    return 'other';
}

global.__deviceShelf = { open: open, reason: reason };
})(typeof window !== 'undefined' ? window : this);
